import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, isRef as _isRef, createBlock as _createBlock, withCtx as _withCtx, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20 max-w-6xl" }
const _hoisted_2 = {
  key: 0,
  class: "mb-6 text-sm text-red-600"
}
const _hoisted_3 = {
  key: 1,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
}
const _hoisted_4 = {
  key: 2,
  class: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6"
}
const _hoisted_5 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm mb-8" }
const _hoisted_6 = { class: "flex flex-wrap items-start justify-between gap-3 p-4 pb-2" }
const _hoisted_7 = { class: "flex-1 min-w-0" }
const _hoisted_8 = { class: "text-sm text-gray-500 mt-0.5" }
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = { class: "px-4 pb-4 h-72" }
const _hoisted_11 = { class: "text-gray-700" }
const _hoisted_12 = { class: "text-gray-700" }
const _hoisted_13 = { class: "text-gray-700" }
const _hoisted_14 = { class: "text-gray-700" }
const _hoisted_15 = { class: "flex justify-end items-center gap-3" }
const _hoisted_16 = ["disabled", "onClick"]
const _hoisted_17 = ["onClick"]

import 'chart.js/auto'

import { computed, ref, toRef } from 'vue'
import { Bar } from 'vue-chartjs'

import EmptyState from '@/components/neue/EmptyState.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import NeueNumberedPagination from '@/components/neue/NeueNumberedPagination.vue'
import NeueTablist from '@/components/neue/NeueTablist.vue'
import NeueWell from '@/components/neue/NeueWell.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import TablistGrid from '@/components/neue/TablistGrid.vue'
import TablistHeader from '@/components/neue/TablistHeader.vue'
import TablistRow from '@/components/neue/TablistRow.vue'
import { PAGE_SIZE, useAgentOverview } from '@/composables/useAgentOverview'
import { useRevelio } from '@/composables/useRevelio'
import api from '@/store/api'
import AgentConversationsTile from '@/view/ai/AgentConversationsTile.vue'


export default {
  __name: 'UpsellOverview',
  props: {
  agentId: { type: String, required: true }
},
  setup(__props) {

const props = __props

const {
  rangeKey,
  rangeOptions,
  error,
  loadingChart,
  loadingList,
  periods,
  totals,
  tiles,
  rows,
  page,
  pageCount,
  totalCount,
  dateRange,
  goToPage,
  refetch
} = useAgentOverview({ agentId: toRef(props, 'agentId'), endpoint: 'ai_upsell_overview' })

const { revealed } = useRevelio()
const confirmingId = ref(null)
const endingId = ref(null)

const endConversation = async row => {
  error.value = ''
  endingId.value = row.id
  try {
    await api.call('POST', `agent_playbook_sessions/${row.id}/end_conversation`)
    await refetch()
  } catch (e) {
    error.value = e?.response?.data?.errors?.[0] || 'Failed to end conversation.'
  } finally {
    endingId.value = null
    confirmingId.value = null
  }
}

const columns = ['Conversation Date', 'Customer', 'Fulfillment Date', 'Outcome', 'Revenue Impact', '']

const chartData = computed(() => {
  if (!periods.value.length && totals.value.conversations === 0) return null

  const start = new Date(dateRange.value.beginning_date)
  const end = new Date(dateRange.value.ending_date)
  const byDate = new Map(periods.value.map(p => [p.date, p]))
  const days = []
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().slice(0, 10)
    days.push(byDate.get(iso) || { date: iso, conversations: 0, items_added: 0, revenue_cents: 0 })
  }
  return {
    labels: days.map(p => formatChartLabel(p.date)),
    datasets: [
      { label: 'Conversations', backgroundColor: '#6366f1', yAxisID: 'y', data: days.map(p => p.conversations) },
      { label: 'Items added', backgroundColor: '#10b981', yAxisID: 'y', data: days.map(p => p.items_added) },
      {
        label: 'Revenue ($)',
        backgroundColor: '#f59e0b',
        yAxisID: 'y1',
        data: days.map(p => (p.revenue_cents || 0) / 100)
      }
    ]
  }
})

const formatChartLabel = iso => {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'top' } },
  scales: {
    y: { beginAtZero: true, position: 'left', title: { display: true, text: 'Count' } },
    y1: {
      beginAtZero: true,
      position: 'right',
      title: { display: true, text: 'Revenue ($)' },
      grid: { drawOnChartArea: false }
    }
  }
}

const periodLabel = computed(() => (tiles.value ? `· ${tiles.value.period_days}D` : ''))

const ratio = (current, prior) => {
  if (prior === undefined || prior === null || prior === 0) return null
  return (current - prior) / prior
}

const aiRepliesValue = computed(() => {
  if (!tiles.value) return '0'
  return Number(tiles.value.current.ai_replies).toLocaleString()
})

const aiRepliesDelta = computed(() => {
  if (!tiles.value) return null
  return ratio(tiles.value.current.ai_replies, tiles.value.prior.ai_replies)
})

const staffHoursValue = computed(() => {
  if (!tiles.value) return '~0.0 hrs'
  const minutes = tiles.value.current.ai_replies * tiles.value.estimated_minutes_per_reply
  return `~${(minutes / 60).toFixed(1)} hrs`
})

const staffHoursCaption = computed(() => {
  if (!tiles.value) return ''
  return `est. ${tiles.value.estimated_minutes_per_reply} min/reply`
})

const revenueValue = computed(() => {
  if (!tiles.value) return '$0'
  const dollars = tiles.value.current.revenue_cents / 100
  return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
})

const revenueDelta = computed(() => {
  if (!tiles.value) return null
  return ratio(tiles.value.current.revenue_cents, tiles.value.prior.revenue_cents)
})

const handoffRateValue = computed(() => {
  if (!tiles.value) return '—'
  const { handoff_sessions, engaged_sessions } = tiles.value.current
  if (engaged_sessions === 0) return '—'
  return `${Math.round((handoff_sessions / engaged_sessions) * 100)}%`
})

// Handoff Rate uses a RELATIVE delta (current / prior - 1). Lower is better → deltaInverted.
const handoffRateDelta = computed(() => {
  if (!tiles.value) return null
  const cur = tiles.value.current
  const prior = tiles.value.prior
  if (cur.engaged_sessions === 0 || prior.engaged_sessions === 0) return null
  const curRate = cur.handoff_sessions / cur.engaged_sessions
  const priorRate = prior.handoff_sessions / prior.engaged_sessions
  if (priorRate === 0) return null
  return (curRate - priorRate) / priorRate
})

// outcomeLabel receives the full attributes object because the "items_added" label
// also needs attrs.items_added_count. Pass row.attributes at every call site.
const outcomeLabel = attrs => {
  if (attrs.outcome === 'items_added')
    return `${attrs.items_added_count} item${attrs.items_added_count === 1 ? '' : 's'} added`
  if (attrs.outcome === 'handoff') return 'Handoff'
  return attrs.outcome.charAt(0).toUpperCase() + attrs.outcome.slice(1)
}

const outcomePillClass = outcome => {
  if (outcome === 'items_added') return 'bg-green-100 text-green-800'
  if (outcome === 'handoff') return 'bg-amber-100 text-amber-800'
  return 'bg-gray-100 text-gray-800'
}

const conversationLink = row => {
  const cup = row.attributes.conversation_user_pair_id
  const msg = row.attributes.first_outbound_message_id
  return msg ? `/messages/${cup}?messageId=${msg}` : `/messages/${cup}`
}

const formatDollars = cents => {
  const dollars = (cents || 0) / 100
  return dollars === 0 ? '-' : `$${dollars.toFixed(2)}`
}

const formatDateTime = iso => (iso ? new Date(iso).toLocaleString() : '-')
const formatDate = iso => (iso ? new Date(iso).toLocaleDateString() : '-')

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_unref(error))
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, _toDisplayString(_unref(error)), 1))
      : _createCommentVNode("", true),
    (!_unref(tiles) && _unref(loadingChart))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (n) => {
            return _createElementVNode("div", {
              key: n,
              class: "bg-white rounded-lg border border-gray-200 p-6 h-32 animate-pulse"
            }, [...(_cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "h-3 w-24 bg-gray-200 rounded mb-3" }, null, -1),
              _createElementVNode("div", { class: "h-8 w-20 bg-gray-300 rounded" }, null, -1)
            ]))])
          }), 64))
        ]))
      : _createCommentVNode("", true),
    (_unref(tiles))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(AgentConversationsTile, {
            label: `AI Replies ${periodLabel.value}`,
            value: aiRepliesValue.value,
            delta: aiRepliesDelta.value
          }, null, 8, ["label", "value", "delta"]),
          _createVNode(AgentConversationsTile, {
            label: "Staff Time Saved",
            value: staffHoursValue.value,
            caption: staffHoursCaption.value
          }, null, 8, ["value", "caption"]),
          _createVNode(AgentConversationsTile, {
            label: "Upsell Revenue",
            value: revenueValue.value,
            delta: revenueDelta.value
          }, null, 8, ["value", "delta"]),
          _createVNode(AgentConversationsTile, {
            label: "Handoff Rate",
            value: handoffRateValue.value,
            delta: handoffRateDelta.value,
            "delta-inverted": true
          }, null, 8, ["value", "delta"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(NeueLoading, { loading: _unref(loadingChart) }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _cache[7] || (_cache[7] = _createElementVNode("h3", { class: "text-lg font-bold text-gray-900" }, "Activity", -1)),
              _createElementVNode("p", _hoisted_8, [
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).conversations), 1),
                _cache[3] || (_cache[3] = _createTextVNode(" conversations · ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).items_added), 1),
                _cache[4] || (_cache[4] = _createTextVNode(" items added · ", -1)),
                _createElementVNode("strong", null, _toDisplayString(formatDollars(_unref(totals).revenue_cents)), 1),
                _cache[5] || (_cache[5] = _createTextVNode(" revenue · ", -1)),
                _createElementVNode("strong", null, _toDisplayString(_unref(totals).handoffs), 1),
                _cache[6] || (_cache[6] = _createTextVNode(" handoffs ", -1))
              ])
            ]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(SelectInput, {
                modelValue: _unref(rangeKey),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => (_isRef(rangeKey) ? (rangeKey).value = $event : null)),
                slim: "",
                options: _unref(rangeOptions).map(opt => ({ name: opt.label, value: opt.value }))
              }, null, 8, ["modelValue", "options"])
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            (chartData.value)
              ? (_openBlock(), _createBlock(_unref(Bar), {
                  key: 0,
                  data: chartData.value,
                  options: chartOptions
                }, null, 8, ["data"]))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueLoading, {
      loading: _unref(loadingList) && !_unref(rows).length
    }, {
      default: _withCtx(() => [
        (_unref(rows).length)
          ? (_openBlock(), _createBlock(NeueTablist, { key: 0 }, {
              head: _withCtx(() => [
                _createVNode(TablistHeader, null, {
                  default: _withCtx(() => [
                    _createVNode(TablistGrid, {
                      columns: columns,
                      header: ""
                    }, {
                      default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                        _createElementVNode("div", null, "Conversation Date", -1),
                        _createElementVNode("div", null, "Customer", -1),
                        _createElementVNode("div", null, "Fulfillment Date", -1),
                        _createElementVNode("div", null, "Outcome", -1),
                        _createElementVNode("div", null, "Revenue Impact", -1),
                        _createElementVNode("div", null, null, -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rows), (row) => {
                  return (_openBlock(), _createBlock(TablistRow, {
                    key: row.id
                  }, {
                    default: _withCtx(() => [
                      _createVNode(TablistGrid, {
                        columns: columns,
                        class: "items-center"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_11, _toDisplayString(formatDateTime(row.attributes.conversation_date)), 1),
                          _createElementVNode("div", _hoisted_12, _toDisplayString(row.attributes.customer_name || '-'), 1),
                          _createElementVNode("div", _hoisted_13, _toDisplayString(formatDate(row.attributes.fulfillment_date)), 1),
                          _createElementVNode("div", null, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["px-2 text-xs leading-5 font-semibold rounded-full", outcomePillClass(row.attributes.outcome)])
                            }, _toDisplayString(outcomeLabel(row.attributes)), 3)
                          ]),
                          _createElementVNode("div", _hoisted_14, _toDisplayString(formatDollars(row.attributes.revenue_added_cents)), 1),
                          _createElementVNode("div", _hoisted_15, [
                            (_unref(revealed) && row.attributes.outcome === 'pending')
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  (confirmingId.value === row.id)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createElementVNode("button", {
                                          class: "text-sm font-medium text-red-600 hover:text-red-700",
                                          disabled: endingId.value === row.id,
                                          onClick: $event => (endConversation(row))
                                        }, _toDisplayString(endingId.value === row.id ? 'Ending...' : 'Confirm end'), 9, _hoisted_16),
                                        _createElementVNode("button", {
                                          class: "text-sm text-gray-500 hover:text-gray-700",
                                          onClick: _cache[1] || (_cache[1] = $event => (confirmingId.value = null))
                                        }, "Cancel")
                                      ], 64))
                                    : (_openBlock(), _createElementBlock("button", {
                                        key: 1,
                                        class: "text-sm font-medium text-red-600 hover:text-red-700",
                                        onClick: $event => (confirmingId.value = row.id)
                                      }, " End ", 8, _hoisted_17))
                                ], 64))
                              : _createCommentVNode("", true),
                            _createVNode(_component_router_link, {
                              to: conversationLink(row),
                              class: "text-sm font-medium text-brand-600 hover:text-brand-700"
                            }, {
                              default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                _createTextVNode(" View → ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["to"])
                          ])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024))
                }), 128))
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(NeueWell, {
              key: 1,
              class: "text-center"
            }, {
              default: _withCtx(() => [
                _createVNode(EmptyState, { plural: "conversations" }),
                _cache[10] || (_cache[10] = _createElementVNode("p", { class: "text-sm text-gray-500 mt-2" }, " No AI Upsell conversations in this date range. They'll appear here once your Upsell agent runs against an order. ", -1))
              ]),
              _: 1
            }))
      ]),
      _: 1
    }, 8, ["loading"]),
    _createVNode(NeueNumberedPagination, {
      page: _unref(page),
      "page-count": _unref(pageCount),
      "total-count": _unref(totalCount),
      "page-size": _unref(PAGE_SIZE),
      loading: _unref(loadingList),
      onChange: _unref(goToPage)
    }, null, 8, ["page", "page-count", "total-count", "page-size", "loading", "onChange"])
  ]))
}
}

}